import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';
import { useChartColors, useFmt, useLabels } from './tmShared';
import type { DayPoint, Evolution, Expected } from '../../services/trafficManagerService';

const TooltipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg px-3 py-2 text-xs text-gray-800 dark:text-gray-100 max-w-[260px]">{children}</div>
);

/** Gasto acumulado do mês x ritmo ideal (linha reta até a verba). */
export const PacingChart: React.FC<{ data: Evolution['mes']; cur: string; budget: number }> = ({ data, cur, budget }) => {
  const { t } = useTranslation();
  const c = useChartColors();
  const f = useFmt();
  const real = t('trafficManager.charts.spentCumulative', 'Gasto acumulado');
  const ideal = t('trafficManager.charts.idealPace', 'Ritmo ideal');
  return (
    <div>
      <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300 mb-2" aria-hidden>
        <span className="inline-flex items-center gap-1.5"><span className="w-4 h-0.5 rounded" style={{ background: c.s1 }} />{real}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed" style={{ borderColor: c.ideal }} />{ideal}</span>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={f.day} tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis tickFormatter={(v) => f.money(v, cur, true)} tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={64} domain={[0, Math.max(budget, ...data.map((d) => d.real || 0)) * 1.05]} />
            <ReferenceLine y={budget} stroke={c.ideal} strokeWidth={1} label={{ value: t('trafficManager.charts.budget', 'Verba'), position: 'insideTopLeft', fill: c.axis, fontSize: 11 }} />
            <Tooltip
              cursor={{ stroke: c.axis, strokeWidth: 1 }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const p = payload[0].payload as Evolution['mes'][number];
                return (
                  <TooltipBox>
                    <div className="font-medium mb-1">{f.day(String(label))}</div>
                    {p.real != null && <div className="flex justify-between gap-4"><span>{real}</span><span className="tabular-nums font-medium">{f.money(p.real, cur)}</span></div>}
                    <div className="flex justify-between gap-4"><span>{ideal}</span><span className="tabular-nums">{f.money(p.ideal, cur)}</span></div>
                  </TooltipBox>
                );
              }}
            />
            <Line type="monotone" dataKey="ideal" stroke={c.ideal} strokeWidth={2} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="real" stroke={c.s1} strokeWidth={2} dot={false} connectNulls={false} activeDot={{ r: 5, stroke: c.surface, strokeWidth: 2 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Conversões acumuladas do mês: real x o que a média da conta entregaria com o
 * mesmo gasto, a projeção no ritmo atual e a meta (se houver). Tudo na mesma
 * unidade (conversões), um eixo só.
 */
export const ExpectedChart: React.FC<{ data: Expected['pontos'] }> = ({ data }) => {
  const { t } = useTranslation();
  const c = useChartColors();
  const f = useFmt();
  const temMeta = data.some((p) => p.meta != null);
  const temEsp = data.some((p) => p.esperado != null);
  const series = [
    { k: 'real' as const, label: t('trafficManager.expected.real', 'Real'), color: c.s1, dash: undefined as string | undefined },
    ...(temEsp ? [{ k: 'esperado' as const, label: t('trafficManager.expected.byAverage', 'Esperado pela média (mesmo gasto)'), color: c.ideal, dash: '5 5' }] : []),
    { k: 'projecao' as const, label: t('trafficManager.expected.projection', 'Projeção no ritmo atual'), color: c.s1, dash: '2 4' },
    ...(temMeta ? [{ k: 'meta' as const, label: t('trafficManager.expected.target', 'Meta'), color: c.s2, dash: '6 3' }] : [])
  ];
  const max = Math.max(1, ...data.flatMap((p) => series.map((s) => Number(p[s.k]) || 0)));
  return (
    <div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300 mb-2" aria-hidden>
        {series.map((s) => (
          <span key={s.k} className="inline-flex items-center gap-1.5">
            <svg width="18" height="6"><line x1="0" x2="18" y1="3" y2="3" stroke={s.color} strokeWidth="2" strokeDasharray={s.dash} /></svg>{s.label}
          </span>
        ))}
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={f.day} tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fill: c.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={40} domain={[0, Math.ceil(max * 1.08)]} />
            <Tooltip
              cursor={{ stroke: c.axis, strokeWidth: 1 }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const p = payload[0].payload as Expected['pontos'][number];
                return (
                  <TooltipBox>
                    <div className="font-medium mb-1">{f.day(String(label))}</div>
                    {series.filter((s) => p[s.k] != null).map((s) => (
                      <div key={s.k} className="flex justify-between gap-4"><span>{s.label}</span><span className="tabular-nums font-medium">{f.dec(p[s.k] as number, 1)}</span></div>
                    ))}
                  </TooltipBox>
                );
              }}
            />
            {series.map((s) => (
              <Line key={s.k} type="monotone" dataKey={s.k} stroke={s.color} strokeWidth={2} strokeDasharray={s.dash} dot={false} connectNulls={false}
                activeDot={s.k === 'real' ? { r: 5, stroke: c.surface, strokeWidth: 2 } : false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

type MetricKey = 'spend' | 'conversions' | 'cpa' | 'cpc' | 'ctr' | 'clicks';

/**
 * Uma métrica dia a dia (um eixo, uma série) com as ações do robô marcadas.
 * Várias métricas viram vários gráficos lado a lado, nunca eixo duplo.
 */
export const MetricChart: React.FC<{
  title: string;
  data: DayPoint[];
  metric: MetricKey;
  kind?: 'line' | 'bar';
  cur: string;
  actions: Evolution['acoes'];
  headline?: string;
  headlineLabel?: string;
}> = ({ title, data, metric, kind = 'line', cur, actions, headline, headlineLabel }) => {
  const { t } = useTranslation();
  const c = useChartColors();
  const f = useFmt();
  const L = useLabels();
  const fmt = (v: number | null | undefined) => {
    if (v == null) return '—';
    if (metric === 'spend' || metric === 'cpa' || metric === 'cpc') return f.money(v, cur);
    if (metric === 'ctr') return `${f.dec(v, 2)}%`;
    return f.int(v);
  };
  const porDia: Record<string, Evolution['acoes']> = {};
  for (const a of actions) (porDia[a.date] = porDia[a.date] || []).push(a);
  const diasComAcao = Object.keys(porDia).filter((d) => data.some((p) => p.date === d));
  const ultimo = [...data].reverse().find((d) => d[metric] != null);
  const Chart = kind === 'bar' ? BarChart : LineChart;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h3>
        {headline != null
          ? <span className="text-right"><span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{headline}</span>{headlineLabel && <span className="block text-[10px] text-gray-500">{headlineLabel}</span>}</span>
          : ultimo && <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{fmt(ultimo[metric] as number)}</span>}
      </div>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <Chart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }} barCategoryGap={2}>
            <CartesianGrid stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={f.day} tick={{ fill: c.axis, fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={28} />
            <YAxis allowDecimals={!(metric === 'conversions' || metric === 'clicks')} tickFormatter={(v) => (metric === 'spend' || metric === 'cpa' || metric === 'cpc' ? f.money(v, cur, true) : metric === 'ctr' ? `${v}%` : f.int(v))} tick={{ fill: c.axis, fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
            {diasComAcao.map((d) => (
              <ReferenceLine key={d} x={d} stroke={c.marker} strokeDasharray="3 3" strokeWidth={1} />
            ))}
            <Tooltip
              cursor={kind === 'bar' ? { fill: c.grid } : { stroke: c.axis, strokeWidth: 1 }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const p = payload[0].payload as DayPoint;
                const acs = porDia[String(label)] || [];
                return (
                  <TooltipBox>
                    <div className="font-medium mb-1">{f.day(String(label))}</div>
                    <div className="flex justify-between gap-4"><span>{title}</span><span className="tabular-nums font-medium">{fmt(p[metric] as number)}</span></div>
                    {acs.length > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700">
                        <div className="text-[11px] text-gray-500 mb-0.5">{t('trafficManager.charts.robotActions', 'Ações do robô neste dia')}</div>
                        {acs.slice(0, 4).map((a, i) => <div key={i} className="truncate">• {L.action(a.tool)}{a.campaign_name ? ` · ${a.campaign_name}` : ''}</div>)}
                        {acs.length > 4 && <div className="text-gray-500">+{acs.length - 4}</div>}
                      </div>
                    )}
                  </TooltipBox>
                );
              }}
            />
            {kind === 'bar'
              ? <Bar dataKey={metric} fill={c.s1} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              : <Line type="monotone" dataKey={metric} stroke={c.s1} strokeWidth={2} dot={false} connectNulls activeDot={{ r: 5, stroke: c.surface, strokeWidth: 2 }} isAnimationActive={false} />}
          </Chart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/** Minilinha de 14 dias por campanha (sem eixos; a tabela traz os números). */
export const Sparkline: React.FC<{ data: { date: string; spend: number }[] }> = ({ data }) => {
  const c = useChartColors();
  if (!data || data.length < 2) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="w-24 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="spend" stroke={c.s1} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
